/**
 * useDeployment Hook
 * AIDLC Administrative Backend
 *
 * Custom hook for the deployment pipeline. Fetches initial state via REST,
 * then subscribes to Socket.io events for real-time build/deploy progress.
 *
 * Socket.io events consumed:
 *   deployment:status   - Pipeline stage changed
 *   deployment:log      - New log entry from build/deploy process
 *   deployment:complete - Build or deployment finished (success or failure)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import * as deploymentService from '../services/deploymentService';
import type { DeploymentState, LogEntry } from '../types/deployment';

const WS_URL = import.meta.env.VITE_WS_URL || '';

const INITIAL_STATE: DeploymentState = {
  status: 'idle',
  deploymentId: null,
  triggeredBy: '',
  startedAt: null,
  stageStartedAt: null,
  error: null,
};

export function useDeployment() {
  const [state, setState] = useState<DeploymentState>(INITIAL_STATE);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  // Fetch initial state + connect to WebSocket on mount
  useEffect(() => {
    // REST: initial state
    deploymentService
      .getDeploymentStatus()
      .then((data) => setState(data))
      .catch((err) => {
        console.warn('[useDeployment] Failed to fetch initial status:', err.message);
      });

    // Socket.io: real-time updates
    if (!WS_URL) {
      console.warn('[useDeployment] VITE_WS_URL not configured. Real-time deployment updates disabled.');
      return;
    }

    const socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      console.info('[useDeployment] Socket connected');
    });

    socket.on('disconnect', (reason) => {
      setIsConnected(false);
      console.info('[useDeployment] Socket disconnected:', reason);
    });

    socket.on('connect_error', (err) => {
      console.error('[useDeployment] Socket connection error:', err.message);
    });

    socket.on('deployment:status', (data: DeploymentState) => {
      setState(data);
    });

    socket.on('deployment:log', (entry: LogEntry) => {
      setLogs((prev) => [...prev, entry]);
    });

    socket.on('deployment:complete', (data: { status: string; error?: string }) => {
      setState((prev) => ({
        ...prev,
        status: data.status === 'success' ? 'deployed' : prev.status,
        error: data.error || prev.error,
      }));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const triggerBuild = useCallback(async () => {
    setLogs([]); // Clear logs for new build
    const result = await deploymentService.triggerBuild();
    return result;
  }, []);

  const triggerDeploy = useCallback(async () => {
    return deploymentService.triggerDeploy();
  }, []);

  const cancel = useCallback(async () => {
    return deploymentService.cancelDeployment();
  }, []);

  const clearLogs = useCallback(() => setLogs([]), []);

  return {
    state,
    logs,
    isConnected,
    triggerBuild,
    triggerDeploy,
    cancel,
    clearLogs,
  };
}
